// Arrival times for the intro build-up: when the build-up front reaches each point of the scene.
//
// The front starts at the surfaces nearest the visitor in every direction (above, below, left, right,
// ahead, behind) and then travels along the surfaces by the shortest path: walls race forward along
// themselves, a ceiling lamp is reached through its mount and builds downwards, the next room is
// reached through the door frame. Crossing open air is allowed but slow, so loose parts still arrive.
//
// Works on a coarse grid (~10 cm) derived from the collision voxels, using Dial's bucket queue with
// integer costs, so it runs in well under a second while the poster is still showing.

const DIRS = [];
for (let dz = -1; dz <= 1; dz++) for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    if (dx || dy || dz) DIRS.push([dx, dy, dz, [0, 10, 14, 17][Math.abs(dx) + Math.abs(dy) + Math.abs(dz)]]);
}
// The same 26 neighbours as flat arrays, for the hot loop
const DX = Int8Array.from(DIRS, d => d[0]), DY = Int8Array.from(DIRS, d => d[1]), DZ = Int8Array.from(DIRS, d => d[2]);
const DW = Uint8Array.from(DIRS, d => d[3]);
const AIR_COST = 3;        // crossing open air costs this much more than travelling along a surface
const MAX_CELLS = 600000;

/**
 * @param {import('../collision/voxel-grid.js').VoxelGrid} grid
 * @param {number[]} eye - visitor eye position (world)
 * @returns {{min:number[], cell:number, nx:number, ny:number, nz:number, times:Float32Array, maxT:number}}
 *          times are in metres of travel, normalised later by the shader
 */
export function computeArrivalField(grid, eye) {
    // Coarse cell size: an integer number of voxels, enough to keep the grid small
    const span = [grid.max[0] - grid.min[0], grid.max[1] - grid.min[1], grid.max[2] - grid.min[2]];
    let k = Math.max(1, Math.round(0.1 / grid.res));
    while (Math.ceil(span[0] / (grid.res * k)) * Math.ceil(span[1] / (grid.res * k)) * Math.ceil(span[2] / (grid.res * k)) > MAX_CELLS) k++;
    const cell = grid.res * k;
    const nx = Math.ceil(span[0] / cell), ny = Math.ceil(span[1] / cell), nz = Math.ceil(span[2] / cell);
    const n = nx * ny * nz;
    const idx = (x, y, z) => (z * ny + y) * nx + x;

    // Solid cells: any solid voxel inside
    const solid = new Uint8Array(n);
    for (let z = 0; z < nz; z++) for (let y = 0; y < ny; y++) for (let x = 0; x < nx; x++) {
        let s = 0;
        for (let vz = z * k; vz < (z + 1) * k && !s; vz++)
            for (let vy = y * k; vy < (y + 1) * k && !s; vy++)
                for (let vx = x * k; vx < (x + 1) * k && !s; vx++)
                    if (grid.solid(vx, vy, vz)) s = 1;
        solid[idx(x, y, z)] = s;
    }
    // Skin: solid cells touching free space. Surfaces seen from inside the rooms.
    const skin = new Uint8Array(n);
    for (let z = 0, i = 0; z < nz; z++) for (let y = 0; y < ny; y++) for (let x = 0; x < nx; x++, i++) {
        if (!solid[i]) continue;
        // The six face neighbours (outside the grid counts as solid)
        if ((x + 1 < nx && !solid[i + 1]) || (x > 0 && !solid[i - 1]) ||
            (y + 1 < ny && !solid[i + nx]) || (y > 0 && !solid[i - nx]) ||
            (z + 1 < nz && !solid[i + nx * ny]) || (z > 0 && !solid[i - nx * ny])) skin[i] = 1;
    }

    // Sources: the surfaces around the visitor (floor below, ceiling above, walls beside), each
    // starting at its straight-line distance. Everything further away is reached along the surfaces,
    // so furniture builds up from where it stands and lamps from where they hang.
    const dist = new Uint32Array(n).fill(0xffffffff);
    const toCell = p => [
        Math.min(nx - 1, Math.max(0, Math.floor((p[0] - grid.min[0]) / cell))),
        Math.min(ny - 1, Math.max(0, Math.floor((p[1] - grid.min[1]) / cell))),
        Math.min(nz - 1, Math.max(0, Math.floor((p[2] - grid.min[2]) / cell)))];
    const RAYS = 3000;
    const hits = [];
    for (let r = 0; r < RAYS; r++) {
        // Fibonacci sphere
        const y = 1 - (2 * (r + 0.5)) / RAYS, rad = Math.sqrt(1 - y * y), phi = r * 2.399963229728653;
        const d = [Math.cos(phi) * rad, y, Math.sin(phi) * rad];
        const hit = grid.raycast(eye, d, 30);
        if (hit) hits.push({ t: hit.t, p: [hit.point[0] + d[0] * 1e-3, hit.point[1] + d[1] * 1e-3, hit.point[2] + d[2] * 1e-3] });
    }
    hits.sort((a, b) => a.t - b.t);
    const nearest = hits.length ? hits[0].t : 0;
    const reach = Math.max(1.8, nearest + 1.0);
    let seeds = hits.filter(h => h.t <= reach);
    if (seeds.length < 20) seeds = hits.slice(0, Math.max(20, hits.length >> 4));
    seeds = seeds.map(h => [toCell(h.p), h.t, Math.round((h.t / cell) * 10)]);

    // Dial's algorithm: one bucket per integer distance, kept as linked lists in typed arrays (no garbage, no
    // per-bucket arrays); each bucket is popped last-in first-out
    let head = new Int32Array(1024).fill(-1);
    let cap = n * 2, entCell = new Int32Array(cap), entNext = new Int32Array(cap), top = 0;
    let cur = Infinity, maxCost = 0;
    const push = (i, c) => {
        if (c >= dist[i]) return;
        dist[i] = c;
        if (c >= head.length) { const h = new Int32Array(Math.max(c + 1, head.length * 2)).fill(-1); h.set(head); head = h; }
        if (top === cap) {
            cap *= 2;
            const a = new Int32Array(cap), b = new Int32Array(cap);
            a.set(entCell); b.set(entNext); entCell = a; entNext = b;
        }
        entCell[top] = i; entNext[top] = head[c]; head[c] = top++;
        if (c < cur) cur = c;
        if (c > maxCost) maxCost = c;
    };
    for (const [[x, y, z], , cost] of seeds) {
        // A seed cell may be the free cell just in front of the surface: start at the solid neighbour
        let i = idx(x, y, z);
        if (!solid[i]) {
            for (const [dx, dy, dz] of DIRS) {
                const X = x + dx, Y = y + dy, Z = z + dz;
                if (X >= 0 && Y >= 0 && Z >= 0 && X < nx && Y < ny && Z < nz && solid[idx(X, Y, Z)]) { i = idx(X, Y, Z); break; }
            }
        }
        push(i, cost);
    }
    const nxy = nx * ny;
    while (cur <= maxCost) {
        const e = head[cur];
        if (e < 0) { cur++; continue; }
        head[cur] = entNext[e];
        const i = entCell[e];
        if (dist[i] !== cur) continue;
        const x = i % nx, y = Math.floor(i / nx) % ny, z = Math.floor(i / nxy);
        const onSkin = skin[i];
        for (let d = 0; d < 26; d++) {
            const X = x + DX[d], Y = y + DY[d], Z = z + DZ[d];
            if (X < 0 || Y < 0 || Z < 0 || X >= nx || Y >= ny || Z >= nz) continue;
            const j = (Z * ny + Y) * nx + X;
            // Fast along surfaces, slow through air or wall interiors
            const c = cur + (onSkin && skin[j] ? DW[d] : DW[d] * AIR_COST);
            if (c >= dist[j]) continue;
            dist[j] = c;
            if (c >= head.length) { const h = new Int32Array(Math.max(c + 1, head.length * 2)).fill(-1); h.set(head); head = h; }
            if (top === cap) {
                cap *= 2;
                const a = new Int32Array(cap), b = new Int32Array(cap);
                a.set(entCell); b.set(entNext); entCell = a; entNext = b;
            }
            entCell[top] = j; entNext[top] = head[c]; head[c] = top++;
            if (c > maxCost) maxCost = c;
        }
    }

    const times = new Float32Array(n);
    let maxT = 0;
    for (let i = 0; i < n; i++) {
        const t = dist[i] === 0xffffffff ? Infinity : (dist[i] / 10) * cell;
        times[i] = t;
        // Normalisation uses surfaces only: interiors and outside space would stretch the timeline
        if (skin[i] && t !== Infinity && t > maxT) maxT = t;
    }
    for (let i = 0; i < n; i++) if (!(times[i] <= maxT)) times[i] = maxT;
    return { min: grid.min.slice(), cell, nx, ny, nz, times, maxT, seeds: seeds.length };
}

/**
 * Pack the field into an RGBA8 atlas (y slices stacked along v): 16-bit normalised time in R/G.
 * Returns {width, height, data}.
 */
export function packArrivalField(field) {
    const { nx, ny, nz, times, maxT } = field;
    const width = nx, height = ny * nz;
    const data = new Uint8Array(width * height * 4);
    for (let z = 0; z < nz; z++) for (let y = 0; y < ny; y++) for (let x = 0; x < nx; x++) {
        const v = Math.round(Math.min(1, times[(z * ny + y) * nx + x] / Math.max(maxT, 1e-6)) * 65535);
        const o = ((y * nz + z) * width + x) * 4;
        data[o] = v >> 8;
        data[o + 1] = v & 255;
        data[o + 3] = 255;
    }
    return { width, height, data };
}
